/**
 * TV1-1982 (ADR-061) — the domain widget grammar as a STANDARD, self-contained JSON
 * Schema, shipped to the web app through the ESM-safe `./analytics/widget-schema`
 * value subpath.
 *
 * ⚠️ GENERATED — do not edit by hand. Source of truth is `UiWidgetSchema` /
 * `UiWidgetsLayoutSchema` in `uiWidgets-generic.types.ts`. Regenerate with:
 *
 *   yarn workspace @bett3r-dev/teselly-domain build
 *   yarn workspace @bett3r-dev/teselly-domain generate:widget-schema
 *
 * Pinned against the LIVE `.plain` schemas by
 * `src/tests/src/analytics/widgetSchema.drift.test.ts`.
 *
 * ## Why materialized, and not `export const X = UiWidgetSchema.plain`
 *
 * Re-exporting `.plain` at runtime would `import { UiWidgetSchema }` (a VALUE) from a
 * module that imports `@bett3r-dev/jsonschema-definer` at its top. jsonschema-definer
 * is CJS-only and breaks Vite's ESM build ("no default export") — the same reason
 * `./analytics/curatedReports` and `./automation/constants` exist. This module is
 * therefore PURE DATA: nothing from the schema layer survives into the web-app bundle.
 *
 * These are the exact objects the server compiles with ajv:
 *  - `UI_WIDGET_JSON_SCHEMA`        == `UiWidgetSchema.plain`        — one widget definition
 *    (the LLM output gate, `teselly-ml-and-ai-library` `validateWidgetDefinition`).
 *  - `UI_WIDGET_LAYOUT_JSON_SCHEMA` == `UiWidgetsLayoutSchema.plain` — a whole layout body
 *    (`AddUiWidgetLayoutCommandSchema`, the PV3 aggregate command gate).
 *
 * Validating against these on the FE with ajv configured identically to the server
 * makes "valid on the server <=> valid on the FE" true by construction.
 */

/** A standard, self-contained JSON Schema object (draft-07-shaped, as jsonschema-definer emits). */
export type WidgetJsonSchema = {readonly [key: string]: unknown };

/** `UiWidgetSchema.plain` — validates ONE domain (wire-form) widget definition. */
export const UI_WIDGET_JSON_SCHEMA: WidgetJsonSchema = {
  anyOf: [
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'lineChart'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'barChart'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'pieChart'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'areaChart'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'donutChart'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'scatterChart'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'criticalStock'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'table'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        measures: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        dimensions: {
          type: 'array',
          items: {
            type: 'string'
          }
        },
        timeDimensions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              dimension: {
                type: 'string'
              },
              granularity: {
                type: 'string',
                enum: [
                  'second',
                  'minute',
                  'hour',
                  'day',
                  'week',
                  'month',
                  'quarter',
                  'year'
                ]
              },
              dateRange: {
                anyOf: [
                  {
                    type: 'string'
                  },
                  {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  }
                ]
              },
              compareDateRange: {
                type: 'array',
                items: {
                  anyOf: [
                    {
                      type: 'string'
                    },
                    {
                      type: 'array',
                      items: {
                        type: 'string'
                      }
                    }
                  ]
                }
              },
              compare: {
                type: 'boolean'
              }
            },
            required: [
              'dimension'
            ]
          }
        },
        filters: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              member: {
                type: 'string'
              },
              operator: {
                type: 'string',
                enum: [
                  'equals',
                  'notEquals',
                  'contains',
                  'notContains',
                  'startsWith',
                  'endsWith',
                  'gt',
                  'gte',
                  'lt',
                  'lte',
                  'set',
                  'notSet',
                  'inDateRange',
                  'notInDateRange',
                  'beforeDate',
                  'beforeOrOnDate',
                  'afterDate',
                  'afterOrOnDate'
                ]
              },
              values: {
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            },
            required: [
              'member',
              'operator'
            ]
          }
        },
        orderBy: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              direction: {
                type: 'string',
                enum: [
                  'asc',
                  'desc'
                ]
              }
            },
            required: [
              'field',
              'direction'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        offset: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'kpi'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        type: {
          type: 'string',
          const: 'text'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true,
          properties: {
            content: {
              type: 'string'
            }
          }
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        type: {
          type: 'string',
          const: 'heading'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        type: {
          type: 'string',
          const: 'spacer'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        collection: {
          type: 'string'
        },
        filter: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              op: {
                type: 'string',
                enum: [
                  'eq',
                  'neq',
                  'lt',
                  'gt',
                  'lte',
                  'gte',
                  'in',
                  'nin',
                  'contains',
                  'notContains',
                  'some',
                  'none',
                  'every',
                  'exists',
                  'notExists',
                  'notEmpty'
                ]
              },
              value: {}
            },
            required: [
              'field',
              'op'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'count'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'collection',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        collection: {
          type: 'string'
        },
        filter: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              op: {
                type: 'string',
                enum: [
                  'eq',
                  'neq',
                  'lt',
                  'gt',
                  'lte',
                  'gte',
                  'in',
                  'nin',
                  'contains',
                  'notContains',
                  'some',
                  'none',
                  'every',
                  'exists',
                  'notExists',
                  'notEmpty'
                ]
              },
              value: {}
            },
            required: [
              'field',
              'op'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'alert'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true,
          properties: {
            severity: {
              type: 'string',
              enum: [
                'low',
                'medium',
                'high'
              ]
            },
            href: {
              type: 'string'
            },
            caption: {
              type: 'string'
            }
          },
          required: [
            'severity'
          ]
        }
      },
      required: [
        'id',
        'name',
        'collection',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        collection: {
          type: 'string'
        },
        filter: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              op: {
                type: 'string',
                enum: [
                  'eq',
                  'neq',
                  'lt',
                  'gt',
                  'lte',
                  'gte',
                  'in',
                  'nin',
                  'contains',
                  'notContains',
                  'some',
                  'none',
                  'every',
                  'exists',
                  'notExists',
                  'notEmpty'
                ]
              },
              value: {}
            },
            required: [
              'field',
              'op'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'list'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'collection',
        'type',
        'widgetComponentProps'
      ]
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: {
          type: 'string',
          format: 'uuid'
        },
        name: {
          type: 'string'
        },
        title: {
          type: 'string'
        },
        description: {
          type: 'string'
        },
        collection: {
          type: 'string'
        },
        filter: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: {
                type: 'string'
              },
              op: {
                type: 'string',
                enum: [
                  'eq',
                  'neq',
                  'lt',
                  'gt',
                  'lte',
                  'gte',
                  'in',
                  'nin',
                  'contains',
                  'notContains',
                  'some',
                  'none',
                  'every',
                  'exists',
                  'notExists',
                  'notEmpty'
                ]
              },
              value: {}
            },
            required: [
              'field',
              'op'
            ]
          }
        },
        limit: {
          type: 'number'
        },
        type: {
          type: 'string',
          const: 'progress'
        },
        widgetComponentProps: {
          type: 'object',
          additionalProperties: true
        }
      },
      required: [
        'id',
        'name',
        'collection',
        'type',
        'widgetComponentProps'
      ]
    }
  ]
};

/** `UiWidgetsLayoutSchema.plain` (== `AddUiWidgetLayoutCommandSchema.plain`) — validates a whole layout body. */
export const UI_WIDGET_LAYOUT_JSON_SCHEMA: WidgetJsonSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    id: {
      type: 'string',
      format: 'uuid'
    },
    parentId: {
      anyOf: [
        {
          type: 'string',
          format: 'uuid'
        },
        {
          type: 'null'
        }
      ]
    },
    type: {
      type: 'string',
      enum: [
        'layout',
        'folder'
      ]
    },
    color: {
      type: 'string'
    },
    icon: {
      type: 'string'
    },
    name: {
      type: 'string'
    },
    description: {
      type: 'string'
    },
    widgets: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          x: {
            type: 'number'
          },
          y: {
            type: 'number'
          },
          width: {
            type: 'number'
          },
          height: {
            type: 'number'
          },
          tint: {
            anyOf: [
              {
                type: 'string',
                enum: [
                  'none',
                  'teal',
                  'amber',
                  'green',
                  'red',
                  'navy',
                  'slate'
                ]
              },
              {
                type: 'string',
                pattern: '^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$'
              }
            ]
          },
          textColor: {
            type: 'string',
            pattern: '^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$'
          },
          definition: {
            anyOf: [
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'lineChart'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'barChart'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'pieChart'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'areaChart'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'donutChart'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'scatterChart'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'criticalStock'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'table'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  measures: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  dimensions: {
                    type: 'array',
                    items: {
                      type: 'string'
                    }
                  },
                  timeDimensions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        dimension: {
                          type: 'string'
                        },
                        granularity: {
                          type: 'string',
                          enum: [
                            'second',
                            'minute',
                            'hour',
                            'day',
                            'week',
                            'month',
                            'quarter',
                            'year'
                          ]
                        },
                        dateRange: {
                          anyOf: [
                            {
                              type: 'string'
                            },
                            {
                              type: 'array',
                              items: {
                                type: 'string'
                              }
                            }
                          ]
                        },
                        compareDateRange: {
                          type: 'array',
                          items: {
                            anyOf: [
                              {
                                type: 'string'
                              },
                              {
                                type: 'array',
                                items: {
                                  type: 'string'
                                }
                              }
                            ]
                          }
                        },
                        compare: {
                          type: 'boolean'
                        }
                      },
                      required: [
                        'dimension'
                      ]
                    }
                  },
                  filters: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        member: {
                          type: 'string'
                        },
                        operator: {
                          type: 'string',
                          enum: [
                            'equals',
                            'notEquals',
                            'contains',
                            'notContains',
                            'startsWith',
                            'endsWith',
                            'gt',
                            'gte',
                            'lt',
                            'lte',
                            'set',
                            'notSet',
                            'inDateRange',
                            'notInDateRange',
                            'beforeDate',
                            'beforeOrOnDate',
                            'afterDate',
                            'afterOrOnDate'
                          ]
                        },
                        values: {
                          type: 'array',
                          items: {
                            type: 'string'
                          }
                        }
                      },
                      required: [
                        'member',
                        'operator'
                      ]
                    }
                  },
                  orderBy: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        direction: {
                          type: 'string',
                          enum: [
                            'asc',
                            'desc'
                          ]
                        }
                      },
                      required: [
                        'field',
                        'direction'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  offset: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'kpi'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  type: {
                    type: 'string',
                    const: 'text'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true,
                    properties: {
                      content: {
                        type: 'string'
                      }
                    }
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  type: {
                    type: 'string',
                    const: 'heading'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  type: {
                    type: 'string',
                    const: 'spacer'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  collection: {
                    type: 'string'
                  },
                  filter: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        op: {
                          type: 'string',
                          enum: [
                            'eq',
                            'neq',
                            'lt',
                            'gt',
                            'lte',
                            'gte',
                            'in',
                            'nin',
                            'contains',
                            'notContains',
                            'some',
                            'none',
                            'every',
                            'exists',
                            'notExists',
                            'notEmpty'
                          ]
                        },
                        value: {}
                      },
                      required: [
                        'field',
                        'op'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'count'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'collection',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  collection: {
                    type: 'string'
                  },
                  filter: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        op: {
                          type: 'string',
                          enum: [
                            'eq',
                            'neq',
                            'lt',
                            'gt',
                            'lte',
                            'gte',
                            'in',
                            'nin',
                            'contains',
                            'notContains',
                            'some',
                            'none',
                            'every',
                            'exists',
                            'notExists',
                            'notEmpty'
                          ]
                        },
                        value: {}
                      },
                      required: [
                        'field',
                        'op'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'alert'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true,
                    properties: {
                      severity: {
                        type: 'string',
                        enum: [
                          'low',
                          'medium',
                          'high'
                        ]
                      },
                      href: {
                        type: 'string'
                      },
                      caption: {
                        type: 'string'
                      }
                    },
                    required: [
                      'severity'
                    ]
                  }
                },
                required: [
                  'id',
                  'name',
                  'collection',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  collection: {
                    type: 'string'
                  },
                  filter: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        op: {
                          type: 'string',
                          enum: [
                            'eq',
                            'neq',
                            'lt',
                            'gt',
                            'lte',
                            'gte',
                            'in',
                            'nin',
                            'contains',
                            'notContains',
                            'some',
                            'none',
                            'every',
                            'exists',
                            'notExists',
                            'notEmpty'
                          ]
                        },
                        value: {}
                      },
                      required: [
                        'field',
                        'op'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'list'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'collection',
                  'type',
                  'widgetComponentProps'
                ]
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  id: {
                    type: 'string',
                    format: 'uuid'
                  },
                  name: {
                    type: 'string'
                  },
                  title: {
                    type: 'string'
                  },
                  description: {
                    type: 'string'
                  },
                  collection: {
                    type: 'string'
                  },
                  filter: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        field: {
                          type: 'string'
                        },
                        op: {
                          type: 'string',
                          enum: [
                            'eq',
                            'neq',
                            'lt',
                            'gt',
                            'lte',
                            'gte',
                            'in',
                            'nin',
                            'contains',
                            'notContains',
                            'some',
                            'none',
                            'every',
                            'exists',
                            'notExists',
                            'notEmpty'
                          ]
                        },
                        value: {}
                      },
                      required: [
                        'field',
                        'op'
                      ]
                    }
                  },
                  limit: {
                    type: 'number'
                  },
                  type: {
                    type: 'string',
                    const: 'progress'
                  },
                  widgetComponentProps: {
                    type: 'object',
                    additionalProperties: true
                  }
                },
                required: [
                  'id',
                  'name',
                  'collection',
                  'type',
                  'widgetComponentProps'
                ]
              }
            ]
          }
        },
        required: [
          'definition'
        ]
      }
    },
    device: {
      type: 'string',
      enum: [
        'web',
        'mobile'
      ],
      description: 'The device type of the Layout in which the widget can be displayed. This is true regardless of the library used to create the widget, it has to do with the width constraints of the device.'
    },
    cols: {
      type: 'number'
    },
    widthPx: {
      type: 'number'
    },
    gap: {
      type: 'number',
      minimum: 0,
      maximum: 48
    }
  },
  required: [
    'id',
    'type',
    'name'
  ]
};
